import React, { useState, useEffect } from 'react';
import { Col, Row, Tag, Spin, Empty } from 'antd';
import { useMutation } from 'react-query';
import BasicDrawer from '../../../components/Drawer';
import { ApiCaller, Colors, Utils } from '../../../config';
import { Button } from '../../../components';
import useColors from '../../../hooks/useColors';
import dayjs from 'dayjs';
import avatar from '../../../assets/images/avatar.jpg';
import { CiViewList } from 'react-icons/ci';
import { IoMdClose } from 'react-icons/io';

const UserPublicationDrawer = ({
    open,
    onClose,
    selectedUser,
    currentConfigOfScheduler,
    onUpdateSuccess,
    onViewUserDetails,
}) => {
    const { colorPrimary } = useColors();
    const [shiftsData, setShiftsData] = useState({
        published: [],
        unpublished: [],
        publishedCount: 0,
        unpublishedCount: 0,
        totalCount: 0,
    });
    const [loading, setLoading] = useState(false);

    const getDateRange = () => {
        const baseDate = currentConfigOfScheduler?.date
            ? dayjs(currentConfigOfScheduler.date)
            : dayjs();
        return {
            startDate: baseDate.startOf('month').format('YYYY-MM-DD'),
            endDate: baseDate.endOf('month').format('YYYY-MM-DD'),
        };
    };

    const fetchShifts = async () => {
        if (!selectedUser?.id) return;
        setLoading(true);
        try {
            const { startDate, endDate } = getDateRange();
            const res = await ApiCaller.Get(
                `/user/${selectedUser.id}/shifts/publication-status`,
            );
            const d = res?.data?.data;
            if (d) {
                setShiftsData({
                    published: Array.isArray(d.published) ? d.published : [],
                    unpublished: Array.isArray(d.unpublished) ? d.unpublished : [],
                    publishedCount: d.publishedCount ?? 0,
                    unpublishedCount: d.unpublishedCount ?? 0,
                    totalCount: d.totalCount ?? 0,
                });
            } else {
                setShiftsData({ published: [], unpublished: [], publishedCount: 0, unpublishedCount: 0, totalCount: 0 });
            }
        } catch (e) {
            setShiftsData({ published: [], unpublished: [], publishedCount: 0, unpublishedCount: 0, totalCount: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && selectedUser?.id) {
            fetchShifts();
        }
    }, [open, selectedUser?.id]);

    const publishAllMutation = useMutation(
        async () => {
            const { startDate, endDate } = getDateRange();
            return ApiCaller.Patch(
                `/user/${selectedUser.id}/shifts/publication-status`,
                { isPublished: true },
            );
        },
        {
            onSuccess: (resp) => {
                Utils.showMessage('success', resp?.data?.message || 'All shifts published successfully');
                fetchShifts();
                onUpdateSuccess?.();
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message || 'Failed to publish shifts');
            },
        },
    );

    const draftAllMutation = useMutation(
        async () => {
            const { startDate, endDate } = getDateRange();
            return ApiCaller.Patch(
                `/user/${selectedUser.id}/shifts/publication-status`,
                { isPublished: false },
            );
        },
        {
            onSuccess: (resp) => {
                Utils.showMessage('success', resp?.data?.message || 'All shifts set to draft');
                fetchShifts();
                onUpdateSuccess?.();
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message || 'Failed to draft shifts');
            },
        },
    );

    const publishedShifts = shiftsData.published;
    const draftShifts = shiftsData.unpublished;

    const formatTime = (time) => {
        if (!time) return '';
        return dayjs(`2000-01-01 ${time}`).format('hh:mm A');
    };

    const ShiftCard = ({ shift, type }) => (
        <div
            style={{
                border: `1px solid ${type === 'published' ? '#52c41a' : '#faad14'}`,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '10px',
                background: type === 'published' ? '#f6ffed' : '#fffbe6',
            }}
        >
            <Row justify="space-between" align="middle">
                <Col>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#333' }}>
                        {shift.shiftDate
                            ? dayjs(shift.shiftDate).format('ddd, MMM D, YYYY')
                            : '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                        {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
                    </div>
                    {shift.property?.name && (
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                            {shift.property.name}
                        </div>
                    )}
                </Col>
                <Col>
                    <Tag
                        color={type === 'published' ? 'success' : 'warning'}
                        style={{ fontWeight: 500, fontSize: '11px' }}
                    >
                        {type === 'published' ? 'Published' : 'Draft'}
                    </Tag>
                </Col>
            </Row>
        </div>
    );

    const { startDate, endDate } = getDateRange();

    return (
        <BasicDrawer
            openDrawer={open}
            width={480}
            hideExtraClose
            title={
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <span
                            className="flex item-center justify-center p-1"
                            style={{ borderRadius: '50%', background: '#FEF3F5' }}
                        >
                            <IoMdClose
                                style={{ color: '#F07080' }}
                                className="cursor-pointer"
                                size={25}
                                onClick={() => onClose()}
                            />
                        </span>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: '10px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <img
                                src={selectedUser?.image || avatar}
                                style={{
                                    width: '34px',
                                    height: '34px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: `2px solid ${colorPrimary}`,
                                }}
                            />
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '15px' }}>
                                    {selectedUser?.name || 'User'}
                                </div>
                                {/* <div style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                                    {startDate} – {endDate}
                                </div> */}
                            </div>
                        </div>
                        <Button.Basic
                            text="User Details"
                            icon={<CiViewList size={16} />}
                            onClick={() => onViewUserDetails?.(selectedUser)}
                            buttonStyle={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                background: colorPrimary,
                                color: 'white',
                                border: 'none',
                                height: '30px',
                                padding: '0 10px',
                                borderRadius: '6px',
                                fontWeight: 500,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                            }}
                        />
                    </div>
                </div>
            }
            onClose={onClose}
            children={
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Action Buttons */}
                    <Row gutter={[12, 0]} style={{ marginBottom: '20px' }}>
                        <Col span={12}>
                            <Button.Basic
                                text="Publish All Drafts"
                                loader={publishAllMutation.isLoading}
                                disabled={draftShifts.length === 0}
                                onClick={() => publishAllMutation.mutate()}
                                buttonStyle={{
                                    width: '100%',
                                    background: draftShifts.length === 0 ? '#d9d9d9' : '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    height: '36px',
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            <Button.Basic
                                text="Draft All Published"
                                loader={draftAllMutation.isLoading}
                                disabled={publishedShifts.length === 0}
                                onClick={() => draftAllMutation.mutate()}
                                buttonStyle={{
                                    width: '100%',
                                    background: publishedShifts.length === 0 ? '#d9d9d9' : '#faad14',
                                    color: 'white',
                                    border: 'none',
                                    height: '36px',
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                }}
                            />
                        </Col>
                    </Row>

                    {/* Summary */}
                    {!loading && shiftsData.totalCount > 0 && (
                        <Row gutter={[12, 0]} style={{ marginBottom: '16px' }}>
                            <Col span={12}>
                                <div
                                    style={{
                                        background: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#52c41a' }}>
                                        {shiftsData.publishedCount}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#555' }}>Published</div>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div
                                    style={{
                                        background: '#fffbe6',
                                        border: '1px solid #ffe58f',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#faad14' }}>
                                        {shiftsData.unpublishedCount}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#555' }}>Draft</div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Shifts List */}
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {loading ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '200px',
                                }}
                            >
                                <Spin size="large" />
                            </div>
                        ) : shiftsData.totalCount === 0 ? (
                            <Empty
                                description="No shifts found for this period"
                                style={{ marginTop: '60px' }}
                            />
                        ) : (
                            <>
                                {draftShifts.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: '#faad14',
                                                marginBottom: '8px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            Draft Shifts ({draftShifts.length})
                                        </div>
                                        {draftShifts.map((shift) => (
                                            <ShiftCard key={shift.id} shift={shift} type="draft" />
                                        ))}
                                    </div>
                                )}

                                {publishedShifts.length > 0 && (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: '#52c41a',
                                                marginBottom: '8px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            Published Shifts ({publishedShifts.length})
                                        </div>
                                        {publishedShifts.map((shift) => (
                                            <ShiftCard key={shift.id} shift={shift} type="published" />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            }
        />
    );
};

export default UserPublicationDrawer;
